import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import type { ListParams } from '@/types/api'
import type {
  Collections,
  Debtor,
  DiscountType,
  FamilyFees,
  FeeCategory,
  FeeDiscount,
  FeeFrequency,
  FeeItem,
  FinanceOverview,
  GenerationResult,
  Invoice,
  InvoiceDetail,
  InvoiceFilters,
  Ledger,
  OutstandingByClass,
  Payment,
  PaymentFilters,
  PaymentMethod,
  Receipt,
} from './types'

/**
 * Financial data is never cached server-side; on the client it is always considered stale so
 * every visit refetches (staleTime 0) and every write invalidates the whole finance tree.
 */
export const financeKeys = {
  all: ['finance'] as const,
  categories: () => [...financeKeys.all, 'categories'] as const,
  items: (params: object) => [...financeKeys.all, 'items', params] as const,
  discounts: () => [...financeKeys.all, 'discounts'] as const,
  invoices: (params: object) => [...financeKeys.all, 'invoices', params] as const,
  invoice: (id: number) => [...financeKeys.all, 'invoice', id] as const,
  periods: () => [...financeKeys.all, 'periods'] as const,
  payments: (params: object) => [...financeKeys.all, 'payments', params] as const,
  payment: (id: number) => [...financeKeys.all, 'payment', id] as const,
  ledger: (studentId: number) => [...financeKeys.all, 'ledger', studentId] as const,
  report: (name: string, params: object = {}) => [...financeKeys.all, 'report', name, params] as const,
  family: (studentId: number) => [...financeKeys.all, 'family', studentId] as const,
}

const live = { staleTime: 0 } as const

export const categoriesQuery = () =>
  queryOptions({
    queryKey: financeKeys.categories(),
    queryFn: () => api.get<FeeCategory[]>('/finance/fee-categories'),
    ...live,
  })
export const feeItemsQuery = (
  params: { academicYearId?: string; categoryId?: string; classId?: string; search?: string } = {},
) =>
  queryOptions({
    queryKey: financeKeys.items(params),
    queryFn: () => api.get<FeeItem[]>('/finance/fee-items', { params: cleanObject(params) }),
    placeholderData: keepPreviousData,
    ...live,
  })
export const discountsQuery = () =>
  queryOptions({
    queryKey: financeKeys.discounts(),
    queryFn: () => api.get<FeeDiscount[]>('/finance/fee-discounts'),
    ...live,
  })

export const invoicesQuery = (params: ListParams & InvoiceFilters) =>
  queryOptions({
    queryKey: financeKeys.invoices(params),
    queryFn: () => api.page<Invoice>('/finance/invoices', params),
    placeholderData: keepPreviousData,
    ...live,
  })
export const invoiceQuery = (id: number) =>
  queryOptions({
    queryKey: financeKeys.invoice(id),
    queryFn: () => api.get<InvoiceDetail>(`/finance/invoices/${id}`),
    ...live,
  })
export const periodsQuery = () =>
  queryOptions({
    queryKey: financeKeys.periods(),
    queryFn: () => api.get<string[]>('/finance/invoices/periods'),
    ...live,
  })

export const paymentsQuery = (params: ListParams & PaymentFilters) =>
  queryOptions({
    queryKey: financeKeys.payments(params),
    queryFn: () => api.page<Payment>('/finance/payments', params),
    placeholderData: keepPreviousData,
    ...live,
  })
export const receiptQuery = (id: number) =>
  queryOptions({
    queryKey: financeKeys.payment(id),
    queryFn: () => api.get<Receipt>(`/finance/payments/${id}`),
    ...live,
  })

export const ledgerQuery = (studentId: number) =>
  queryOptions({
    queryKey: financeKeys.ledger(studentId),
    queryFn: () => api.get<Ledger>(`/finance/students/${studentId}/ledger`),
    ...live,
  })

export const overviewQuery = () =>
  queryOptions({
    queryKey: financeKeys.report('summary'),
    queryFn: () => api.get<FinanceOverview>('/finance/reports/summary'),
    ...live,
  })
export const outstandingByClassQuery = (academicYearId?: string) =>
  queryOptions({
    queryKey: financeKeys.report('outstanding-by-class', { academicYearId }),
    queryFn: () =>
      api.get<OutstandingByClass[]>('/finance/reports/outstanding-by-class', {
        params: cleanObject({ academicYearId }),
      }),
    ...live,
  })
export const collectionsQuery = (from: string, to: string) =>
  queryOptions({
    queryKey: financeKeys.report('collections', { from, to }),
    queryFn: () => api.get<Collections>('/finance/reports/collections', { params: { from, to } }),
    placeholderData: keepPreviousData,
    ...live,
  })
export const topDebtorsQuery = (limit = 10) =>
  queryOptions({
    queryKey: financeKeys.report('top-debtors', { limit }),
    queryFn: () => api.get<Debtor[]>('/finance/reports/top-debtors', { params: { limit } }),
    ...live,
  })

export const familyFeesQuery = (studentId: number) =>
  queryOptions({
    queryKey: financeKeys.family(studentId),
    queryFn: () => api.get<FamilyFees>(`/me/family/students/${studentId}/fees`),
    ...live,
  })

function cleanObject(params: Record<string, string | undefined>): Record<string, string> {
  return Object.fromEntries(Object.entries(params).filter((e): e is [string, string] => Boolean(e[1])))
}

export interface CategoryInput {
  name: string
  isActive: boolean
}
export interface FeeItemInput {
  categoryId: number
  academicYearId: number
  classId: number | null
  name: string
  amount: string
  frequency: FeeFrequency
  dueDay: number | null
  isMandatory: boolean
  isActive: boolean
}
export interface DiscountInput {
  name: string
  type: DiscountType
  value: string
  maxAmount: string | null
  isActive: boolean
}
export interface GenerateInput {
  academicYearId: number
  classId: number | null
  period: string
  issueDate: string | null
  dueDate: string | null
  includeOptional: boolean
}
export interface PaymentInput {
  amount: string
  method: PaymentMethod
  paidOn: string
  reference: string | null
  note: string | null
}

function useFinanceMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: financeKeys.all })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export const useSaveCategory = () =>
  useFinanceMutation(({ id, ...input }: CategoryInput & { id?: number }) =>
    id
      ? api.put<FeeCategory>(`/finance/fee-categories/${id}`, input)
      : api.post<FeeCategory>('/finance/fee-categories', input),
  )
export const useDeleteCategory = () =>
  useFinanceMutation((id: number) => api.delete(`/finance/fee-categories/${id}`))

export const useSaveFeeItem = () =>
  useFinanceMutation(({ id, ...input }: FeeItemInput & { id?: number }) =>
    id ? api.put<FeeItem>(`/finance/fee-items/${id}`, input) : api.post<FeeItem>('/finance/fee-items', input),
  )
export const useSetFeeItemActive = () =>
  useFinanceMutation(({ id, isActive }: { id: number; isActive: boolean }) =>
    api.patch<FeeItem>(`/finance/fee-items/${id}/active`, { isActive }),
  )
export const useDeleteFeeItem = () =>
  useFinanceMutation((id: number) => api.delete(`/finance/fee-items/${id}`))

export const useSaveDiscount = () =>
  useFinanceMutation(({ id, ...input }: DiscountInput & { id?: number }) =>
    id
      ? api.put<FeeDiscount>(`/finance/fee-discounts/${id}`, input)
      : api.post<FeeDiscount>('/finance/fee-discounts', input),
  )
export const useDeleteDiscount = () =>
  useFinanceMutation((id: number) => api.delete(`/finance/fee-discounts/${id}`))

/** Dry run: no writes, so nothing to invalidate. */
export const usePreviewGeneration = () =>
  useMutation({
    mutationFn: (input: GenerateInput) =>
      api.post<GenerationResult>('/finance/invoices/generate/preview', input),
  })
export const useGenerateInvoices = () =>
  useFinanceMutation((input: GenerateInput) =>
    api.post<GenerationResult>('/finance/invoices/generate', input),
  )
export const useCancelInvoice = () =>
  useFinanceMutation(({ id, reason }: { id: number; reason: string }) =>
    api.post<InvoiceDetail>(`/finance/invoices/${id}/cancel`, { reason }),
  )
export const useRecordPayment = () =>
  useFinanceMutation(({ invoiceId, ...input }: PaymentInput & { invoiceId: number }) =>
    api.post<Payment>(`/finance/invoices/${invoiceId}/payments`, input),
  )
export const useReversePayment = () =>
  useFinanceMutation(({ id, reason }: { id: number; reason: string }) =>
    api.post<Receipt>(`/finance/payments/${id}/reverse`, { reason }),
  )
export const useAssignStudentDiscount = () =>
  useFinanceMutation(({ studentId, discountId }: { studentId: number; discountId: number | null }) =>
    api.put<Ledger>(`/finance/students/${studentId}/discount`, { discountId }),
  )
